import { useEffect, useRef } from "react";

import { usePointerReorder } from "../../../shared/mediaReorder/usePointerReorder";
import { MEDIA_CEILING } from "../lib/publicProjection";
import {
  moveMediaByOffset,
  moveMediaToFront,
  orderMediaPickerImages,
} from "../lib/presentationMedia";

/**
 * Presentation-local media selection + drag/keyboard reorder.
 * Never mutates PropertyImage.is_main — only presentation mediaIds order.
 */
function PresentationMediaPicker({
  images,
  mediaIds,
  onChange,
  error,
}) {
  const selectedSet = new Set(mediaIds || []);
  const mediaIdsRef = useRef(mediaIds || []);
  const onChangeRef = useRef(onChange);
  const pickerRef = useRef(null);
  const visibleImages = (Array.isArray(images) ? images : []).slice(0, MEDIA_CEILING);
  const orderedImages = orderMediaPickerImages(visibleImages, mediaIds);

  useEffect(() => {
    mediaIdsRef.current = mediaIds || [];
  }, [mediaIds]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const patchIds = (next) => {
    mediaIdsRef.current = next;
    onChangeRef.current?.(next);
  };

  const { draggingId, onHandlePointerDown, onHandlePointerUp, endDrag } = usePointerReorder({
    rootRef: pickerRef,
    getIds: () => mediaIdsRef.current || [],
    onReorder: (next) => patchIds(next),
    enabled: true,
  });

  const toggleMedia = (imageId) => {
    const current = mediaIdsRef.current || [];
    if (current.includes(imageId)) {
      patchIds(current.filter((id) => id !== imageId));
      return;
    }
    if (current.length >= MEDIA_CEILING) return;
    patchIds([...current, imageId]);
  };

  const makeHero = (imageId) => {
    patchIds(moveMediaToFront(mediaIdsRef.current || [], imageId));
  };

  const onHandleKeyDown = (event, imageId) => {
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      patchIds(moveMediaByOffset(mediaIdsRef.current || [], imageId, -1));
      return;
    }
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      patchIds(moveMediaByOffset(mediaIdsRef.current || [], imageId, 1));
    }
  };

  return (
    <div className="presentation-media-picker-wrap">
      {error ? (
        <p className="presentation-form__error" role="alert">
          {error}
        </p>
      ) : null}
      <ul
        ref={pickerRef}
        className="presentation-media-picker"
        data-testid="presentation-media-picker"
      >
        {orderedImages.map((image) => {
          const selected = selectedSet.has(image.id);
          const selectedIndex = (mediaIds || []).indexOf(image.id);
          const isHero = selected && selectedIndex === 0;
          return (
            <li
              key={image.id}
              className={[
                selected ? "is-selected" : "",
                draggingId === image.id ? "is-dragging" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              data-media-id={image.id}
              data-testid={`presentation-media-item-${image.id}`}
            >
              <label className="presentation-media-picker__card">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleMedia(image.id)}
                />
                <img src={image.image} alt="" />
                <span className="presentation-media-picker__kind">
                  {image.kind === "plan" ? "План" : "Фото"}
                </span>
                {isHero ? (
                  <span className="presentation-media-picker__hero">Головне</span>
                ) : null}
                {selected ? (
                  <span className="presentation-media-picker__check" aria-hidden="true">
                    <i className="fa-solid fa-check" />
                  </span>
                ) : null}
              </label>
              {selected ? (
                <div className="presentation-media-picker__actions">
                  <button
                    type="button"
                    className="presentation-media-picker__handle"
                    aria-label="Перетягнути для зміни порядку. Стрілки вліво/вправо також змінюють порядок."
                    data-testid={`presentation-media-handle-${image.id}`}
                    onPointerDown={(event) => onHandlePointerDown(event, image.id)}
                    onPointerUp={onHandlePointerUp}
                    onPointerCancel={onHandlePointerUp}
                    onLostPointerCapture={(event) => {
                      if (event.buttons) return;
                      endDrag();
                    }}
                    onKeyDown={(event) => onHandleKeyDown(event, image.id)}
                  >
                    <i className="fa-solid fa-grip-vertical" aria-hidden="true" />
                  </button>
                  {!isHero ? (
                    <button
                      type="button"
                      className="action-btn presentation-media-picker__make-main"
                      data-testid={`presentation-media-make-main-${image.id}`}
                      onClick={() => makeHero(image.id)}
                    >
                      Зробити головною
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default PresentationMediaPicker;
